import type { MarkerConfigEntry, SiteCategory } from '../types'
import { REFERENCE_ZOOM } from './mapConfig'

// Shape used together with color — never color alone (accessibility).
// Each category has a pairwise-distinct shape silhouette (circle/diamond/square/house/triangle)
// so categories are distinguishable without relying on hue.
export const MARKER_CONFIG: Record<SiteCategory, MarkerConfigEntry> = {
  OTHER_AT: {
    color: '#d97a06',        // amber
    shape: 'circle',
    label: 'Other AT Sites',
  },
  RESEARCH_LAB: {
    color: '#6d28d9',        // violet
    shape: 'diamond',
    label: 'Research Labs',
  },
  SMART_LIVING: {
    color: '#0d9488',        // teal
    shape: 'square',
    label: 'Smart Living Sites',
  },
  HOUSING: {
    color: '#004090',        // Inglis blue
    shape: 'house',
    label: 'Tech-Integrated Accessible Housing',
  },
  AT_RESOURCE_CENTER: {
    color: '#be123c',        // rose/crimson — distinct hue from every existing category
    shape: 'triangle',
    label: 'AT Resource Centers',
  },
}

export const MARKER_SIZE = 26  // px, baseline at REFERENCE_ZOOM — unchanged from today's fixed size
export const MIN_MARKER_SIZE = 14
export const MAX_MARKER_SIZE = 34
const SIZE_ZOOM_SLOPE = 4  // px of size change per whole zoom level away from REFERENCE_ZOOM

/**
 * Marker visual size as a function of current zoom — anchored at REFERENCE_ZOOM so that at
 * zoom === REFERENCE_ZOOM (7.1) exactly, the result is pixel-identical to today's fixed 26px
 * marker. Note this does NOT guarantee the app's actual default view renders at 26px: on load,
 * MapContainer's fitBounds call picks a zoom that varies by viewport (e.g. measured ~7.4 on a
 * 1440px desktop, ~6.9 on a 1024px tablet, ~5.18 on a 390px phone) and is not currently pinned
 * to REFERENCE_ZOOM. Grows toward MAX_MARKER_SIZE zooming in, shrinks toward MIN_MARKER_SIZE
 * zooming out.
 */
export function zoomScaledMarkerSize(zoom: number): number {
  const size = MARKER_SIZE + (zoom - REFERENCE_ZOOM) * SIZE_ZOOM_SLOPE
  return Math.max(MIN_MARKER_SIZE, Math.min(MAX_MARKER_SIZE, size))
}
